import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import TextField from '@mui/material/TextField';
import { SectionBase } from './SectionBase';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { TYPOGRAPHY } from 'shared/design-system/settings/variables/Typography';
import TrueImpactAnimation from 'shared/design-system/base/TrueImpactAnimation';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import TrueImpactTextIndicator from 'shared/design-system/components/TrueImpactTextIndicator';
import { useProgram } from 'shared/providers/ProgramProvider';
import DonorNameHighlight from '../../ReportsBeta/LevelOneFeedback/DonorNameHighlight';

export const MIN_CHARACTER_DESCRIPTION = 150;
export const MAX_CHARACTER_DESCRIPTION = 900;
export const LengthCounter = ({
  length,
  minLength = MIN_CHARACTER_DESCRIPTION,
  maxLength = MAX_CHARACTER_DESCRIPTION,
}) => {
  return (
    <CharacterCountWrapper>
      <TrueImpactText
        variant="body-small"
        align="right"
        color={
          length <= 0
            ? 'greyDark'
            : length < minLength
            ? 'critical'
            : length > maxLength
            ? 'critical'
            : 'greyDark'
        }
      >
        {length}
        <span className="minCharacter">/{maxLength}</span>
      </TrueImpactText>
      <CounterCircle>
        <svg
          height="100%"
          viewBox="0 0 20 20"
          width="100%"
          style={{ overflow: 'visible' }}
        >
          <circle
            cx="50%"
            cy="50%"
            fill="none"
            stroke-width="2"
            r="9"
            stroke={
              length <= 0
                ? COLORS.greyLight
                : length < minLength
                ? COLORS.criticalBackground
                : length > maxLength
                ? COLORS.criticalBackground
                : COLORS.highlightBackground
            }
          ></circle>
          <circle
            cx="50%"
            cy="50%"
            fill="none"
            stroke-width="2"
            r="9"
            stroke={
              length <= 0
                ? COLORS.greyDark
                : length < minLength
                ? COLORS.critical
                : length > maxLength
                ? COLORS.critical
                : COLORS.highlight
            }
            stroke-linecap="round"
            style={{
              strokeDashoffset:
                length < maxLength
                  ? 56.5487 - (56.5487 / maxLength) * length
                  : 0,
              strokeDasharray: 56.5487,
            }}
          ></circle>
        </svg>
      </CounterCircle>
    </CharacterCountWrapper>
  );
};

const ValidatedTextArea = ({ value, handleText, handleChange, disabled }) => {
  const onChange = useCallback(
    e => {
      const { value } = e.target;
      handleText(value);
      handleChange(
        value.length < MIN_CHARACTER_DESCRIPTION ||
          value.length > MAX_CHARACTER_DESCRIPTION
          ? undefined
          : value,
      );
    },
    [handleChange, handleText],
  );
  return (
    <>
      <TextArea value={value} onChange={onChange} disabled={disabled} />
    </>
  );
};

export const SectionLongText = ({
  title,
  subTitle,
  helpText,
  error,
  questionId,
  value = '',
  onChange,
  guidanceText,
  isProgramLevelQuestion,
}) => {
  const [text, setText] = useState(value);
  const { answers } = useProgram();
  const programAnswerText = answers?.[questionId]?.text ?? '';
  const seededRef = useRef(false);

  const handleChange = useCallback(
    value => {
      onChange(questionId, value);
    },
    [questionId, onChange],
  );

  // One-shot seed from the program-level answer when the report has no
  // answer yet. Keeps the field editable — anything the user types
  // afterward is saved as the report's own answer.
  useEffect(() => {
    if (seededRef.current) return;
    if (!isProgramLevelQuestion) return;
    if (!programAnswerText) return;
    if (value) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    setText(programAnswerText);
    const isValid =
      programAnswerText.length >= MIN_CHARACTER_DESCRIPTION &&
      programAnswerText.length <= MAX_CHARACTER_DESCRIPTION;
    handleChange(isValid ? programAnswerText : undefined);
  }, [isProgramLevelQuestion, programAnswerText, value, handleChange]);

  const handleClear = e => {
    setText('');
  };

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={helpText}
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      <TrueImpactAnimation variant="appear" animationDelay={5}>
        <TextAreaWrapper>
          <DonorNameHighlight
            text={text}
            questionId={questionId}
            className="donor-name-highlight"
          >
            <ValidatedTextArea
              value={text}
              handleChange={handleChange}
              handleText={setText}
            />
          </DonorNameHighlight>
          <TrueImpactTextIndicator
            text={text}
            minLength={MIN_CHARACTER_DESCRIPTION}
            maxLength={MAX_CHARACTER_DESCRIPTION}
            handleClear={handleClear}
          />
        </TextAreaWrapper>
      </TrueImpactAnimation>
    </SectionBase>
  );
};

const TextAreaWrapper = styled.div`
  width: 100%;
  max-width: 540px;
  .minCharacter {
    color: ${COLORS.greyDark};
  }
`;
const CharacterCountWrapper = styled.div`
  min-width: 100px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: ${SPACERS.small};
`;

const CounterCircle = styled.div`
  display: block;
  width: 24px;
  height: 24px;
  transform: rotate(-90deg);
`;

export const TextArea = styled(TextField).attrs({
  fullWidth: true,
  multiline: true,
  rows: 10,
})`
  align-self: center;
  background-color: ${COLORS.greyLightest} !important;
  border: none !important;
  font-size: ${TYPOGRAPHY.body};
  width: 100%;
`;

// TODOs:
//  - Need to add some sort of visual feeback for the amount of characters currently in the textarea while editing
// NICE TO HAVES:
//  - Twitter compose experience that shows when approaching max length as well. What is the maximum length?
// - Provide feedback/indicator while description is being edited using some sort of AI: "Great description" if certain thresholds (ex: 240
// characters) and language are used, etc.
